<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import tCardMultiSvg from '/assets/t-card-icon-multi.svg?raw'
import SyncIndicator from '@/components/common/SyncIndicator.vue'

const route = useRoute()
const router = useRouter()

const tabs = [
  { name: 'incident-overview',    label: 'Overview',   icon: 'dashboard' },
  { name: 'incident-subjects',    label: 'Subjects',   icon: 'person_search' },
  { name: 'incident-teams',       label: 'Teams',      icon: null, customSvg: tCardMultiSvg },
  { name: 'incident-assignments', label: 'Assignments',icon: 'map' },
  { name: 'incident-comms',       label: 'Comms',      icon: 'chat' },
  { name: 'incident-clues',       label: 'Clues',      icon: 'location_on' },
  { name: 'incident-forms',       label: 'Forms',      icon: 'description' },
  { name: 'incident-settings',    label: 'Settings',   icon: 'settings' },
]

const formItems = [
  { label: 'Incident Briefing',     code: 'ICS 201',    route: 'incident-form-ics201' },
  { label: 'Agency Check-In List',  code: 'ICS 211',    route: 'incident-form-ics211' },
  { label: 'Activity Log',          code: 'ICS 214',    route: 'incident-form-ics214' },
  { label: 'Team Assignments',      code: 'SAR 104S',   route: 'incident-form-sar104s-list' },
  { label: 'Team Debrief',          code: 'SAR 110',    route: 'incident-form-sar110-list' },
  { label: 'K9 Team Debrief',       code: 'SAR 111',    route: 'incident-form-sar111-list' },
  { label: 'Clue / Evidence Report',code: 'SAR 134/135',route: 'incident-form-sar134' },
]

const settingsItems = [
  { label: 'General',    icon: 'edit_note', route: 'incident-settings-general' },
  { label: 'Operations', icon: 'schedule',  route: 'incident-settings-operations' },
  { label: 'Personnel',  icon: 'groups',    route: 'incident-settings-personnel' },
  { label: 'Audit Log',  icon: 'history',   route: 'incident-settings-audit' },
]

const activeTab = computed(() => {
  const name = route.name as string
  if (name?.startsWith('incident-form')) return 'incident-forms'
  if (name === 'incident-subject-detail') return 'incident-subjects'
  if (name?.startsWith('incident-settings')) return 'incident-settings'
  return name
})

const showFormsMenu = ref(false)
const formsMenuTop = ref(0)
const formsMenuTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

function onFormsEnter(event: MouseEvent) {
  if (formsMenuTimeout.value) clearTimeout(formsMenuTimeout.value)
  formsMenuTop.value = (event.currentTarget as HTMLElement).getBoundingClientRect().top
  showFormsMenu.value = true
}

function onFormsLeave() {
  formsMenuTimeout.value = setTimeout(() => { showFormsMenu.value = false }, 80)
}

function navigateToForm(routeName: string) {
  router.push({ name: routeName, params: route.params })
  showFormsMenu.value = false
}

const showSettingsMenu = ref(false)
const settingsMenuTop = ref(0)
const settingsMenuTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

function onSettingsEnter(event: MouseEvent) {
  if (settingsMenuTimeout.value) clearTimeout(settingsMenuTimeout.value)
  settingsMenuTop.value = (event.currentTarget as HTMLElement).getBoundingClientRect().top
  showSettingsMenu.value = true
}

function onSettingsLeave() {
  settingsMenuTimeout.value = setTimeout(() => { showSettingsMenu.value = false }, 80)
}

function navigateToSetting(routeName: string) {
  router.push({ name: routeName, params: route.params })
  showSettingsMenu.value = false
}
</script>

<template>
  <!-- Desktop sidebar (hidden on mobile) -->
  <aside class="hidden md:flex fixed left-0 top-10 bottom-8 w-20 flex-col items-center py-2 z-40 bg-surface border-r border-border overflow-y-auto">
    <nav class="flex flex-col gap-0.5 w-full">
      <template v-for="tab in tabs" :key="tab.name">
        <!-- Forms tab with flyout submenu -->
        <div
          v-if="tab.name === 'incident-forms'"
          class="w-full"
          @mouseenter="onFormsEnter"
          @mouseleave="onFormsLeave"
        >
          <button
            :class="[
              'flex flex-col items-center justify-center gap-0.5 w-full py-3 transition-all focus:outline-none',
              activeTab === 'incident-forms'
                ? 'bg-primary-container text-white'
                : 'text-outline hover:text-on-surface hover:bg-surface-container-low',
            ]"
            @click="router.push({ name: 'incident-forms', params: route.params })"
          >
            <span class="material-symbols-outlined text-[42px]">description</span>
            <span class="font-mono text-[0.5rem] uppercase tracking-wide leading-tight">Forms</span>
          </button>
        </div>

        <!-- Settings tab with flyout submenu -->
        <div
          v-else-if="tab.name === 'incident-settings'"
          class="w-full"
          @mouseenter="onSettingsEnter"
          @mouseleave="onSettingsLeave"
        >
          <button
            :class="[
              'flex flex-col items-center justify-center gap-0.5 w-full py-3 transition-all focus:outline-none',
              activeTab === 'incident-settings'
                ? 'bg-primary-container text-white'
                : 'text-outline hover:text-on-surface hover:bg-surface-container-low',
            ]"
            @click="router.push({ name: 'incident-settings', params: route.params })"
          >
            <span class="material-symbols-outlined text-[42px]">info</span>
            <span class="font-mono text-[0.5rem] uppercase tracking-wide leading-tight">Incident Settings</span>
          </button>
        </div>

        <!-- All other tabs -->
        <button
          v-else
          :class="[
            'flex flex-col items-center justify-center gap-0.5 w-full py-3 transition-all focus:outline-none',
            activeTab === tab.name
              ? 'bg-primary-container text-white'
              : 'text-outline hover:text-on-surface hover:bg-surface-container-low',
          ]"
          @click="router.push({ name: tab.name, params: route.params })"
        >
          <span
            v-if="tab.customSvg"
            class="w-7 h-7"
            v-html="tab.customSvg"
          />
          <span v-else class="material-symbols-outlined text-[42px]">{{ tab.icon }}</span>
          <span class="font-mono text-[0.5rem] uppercase tracking-wide leading-tight">{{ tab.label }}</span>
        </button>
      </template>
    </nav>
  </aside>

  <!-- Mobile bottom tab bar (hidden on desktop) -->
  <nav class="flex md:hidden fixed bottom-0 left-0 right-0 h-14 z-40 bg-surface border-t border-border items-stretch">
    <div class="flex flex-1 overflow-x-auto items-stretch">
      <template v-for="tab in tabs" :key="tab.name">
        <button
          :class="[
            'flex flex-col items-center justify-center gap-0.5 px-3 min-w-[3.5rem] shrink-0 transition-all focus:outline-none',
            activeTab === tab.name
              ? 'text-primary'
              : 'text-outline',
          ]"
          @click="router.push({ name: tab.name, params: route.params })"
        >
          <span
            v-if="tab.customSvg"
            class="w-5 h-5"
            v-html="tab.customSvg"
          />
          <span v-else class="material-symbols-outlined text-[22px] leading-none">{{ tab.icon }}</span>
          <span class="font-mono text-[0.45rem] uppercase tracking-wide leading-tight">{{ tab.label }}</span>
        </button>
      </template>
    </div>
    <!-- Sync indicator on the right -->
    <div class="flex items-center px-3 shrink-0 border-l border-border">
      <SyncIndicator />
    </div>
  </nav>

  <!-- Forms flyout — rendered outside aside to escape overflow clipping -->
  <Teleport to="body">
    <div
      v-show="showFormsMenu"
      class="fixed z-50 bg-surface border border-border shadow-lg rounded overflow-hidden min-w-[14rem]"
      :style="{ left: '5rem', top: `${formsMenuTop}px` }"
      @mouseenter="onFormsEnter"
      @mouseleave="onFormsLeave"
    >
      <div class="px-3 py-1.5 border-b border-border">
        <span class="font-mono text-[0.6rem] uppercase tracking-widest text-outline">Forms</span>
      </div>
      <button
        v-for="item in formItems"
        :key="item.route"
        class="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-surface-container-low transition-colors"
        @click="navigateToForm(item.route)"
      >
        <span class="font-mono text-[0.6rem] text-outline w-16 shrink-0">{{ item.code }}</span>
        <span class="font-body text-sm text-on-surface">{{ item.label }}</span>
      </button>
    </div>

    <!-- Settings flyout -->
    <div
      v-show="showSettingsMenu"
      class="fixed z-50 bg-surface border border-border shadow-lg rounded overflow-hidden min-w-[14rem]"
      :style="{ left: '5rem', top: `${settingsMenuTop}px` }"
      @mouseenter="onSettingsEnter"
      @mouseleave="onSettingsLeave"
    >
      <div class="px-3 py-1.5 border-b border-border">
        <span class="font-mono text-[0.6rem] uppercase tracking-widest text-outline">Settings</span>
      </div>
      <button
        v-for="item in settingsItems"
        :key="item.route"
        class="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-surface-container-low transition-colors"
        @click="navigateToSetting(item.route)"
      >
        <span class="material-symbols-outlined text-[14px] text-outline shrink-0">{{ item.icon }}</span>
        <span class="font-body text-sm text-on-surface">{{ item.label }}</span>
      </button>
    </div>
  </Teleport>
</template>
